/**
 * Soma planner adoption — pure shapes + wording (no imports, Node-testable).
 *
 * The data is omon-api /v1/soma_adoption, which serves the hourly
 * `_ops/soma_adoption` summary (app.dsr/dsr/adoption.py) with names
 * resolved. Stages are the FIRST step a client is stuck at.
 */

export type Stage = "no-plan" | "no-mandatory" | "no-shifts" | "not-used" | "in-use";

export interface ClientRow {
  eocId: string;
  name: string | null;
  stage: Stage | string | null;
  shiftsDue: number;
  shiftsEntered: number;
  shiftsComplete: number;
}

export interface ManagerRow {
  id: string;
  name: string | null;
  clients: number;
  withPlan: number;
  withMandatory: number;
  inUse: number;
  shiftsDue: number;
  shiftsEntered: number;
  shiftsComplete: number;
  clientRows: ClientRow[];
}

export interface AdoptionResponse {
  available: boolean;
  generatedAt?: string | null;
  window?: { from: string; to: string } | null;
  totals?: Record<string, number> | null;
  managers?: ManagerRow[];
}

/** What the planner (or the rota) needs to do next, in the business's words. */
export const STAGE_LABEL: Record<Stage, string> = {
  "no-plan": "No plan yet",
  "no-mandatory": "Plan has nothing mandatory",
  "no-shifts": "No shifts due this week",
  "not-used": "Plan ready, nothing entered",
  "in-use": "In use",
};

export function stageLabel(s: string | null): string {
  return s && s in STAGE_LABEL ? STAGE_LABEL[s as Stage] : "Unknown";
}

export function managerLabel(m: Pick<ManagerRow, "id" | "name">): string {
  if (m.id === "unassigned") return "No care manager in OneTouch";
  if (m.name) return m.name;
  // A non-person id (e.g. a template placeholder seen live 2026-09-22) is a
  // data fault to fix, never a blank.
  return m.id.startsWith("_") ? "Not a real care manager (data fault)" : `Unnamed (${m.id})`;
}

/** "3 of 12" — and a 0–100 width for the bar; 0 when there is no base. */
export function ofLabel(n: number, of: number): string {
  return `${n} of ${of}`;
}

export function pct(n: number, of: number): number {
  return of > 0 ? Math.round((100 * n) / of) : 0;
}

/** "15–22 Sep" from ISO dates; tolerant of a missing window. */
export function windowLabel(w: { from: string; to: string } | null | undefined): string {
  if (!w) return "this week";
  const f = new Date(`${w.from}T12:00:00Z`);
  const t = new Date(`${w.to}T12:00:00Z`);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return "this week";
  // Fixed names, not toLocaleString: ICU versions disagree ("Sep" vs "Sept").
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${f.getUTCDate()}–${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`;
}
